'use client'

import { LotTrustSignals } from '~/app/accounting/lot-trust-signals'
import { Message } from '~/components/component-library'

const describeSignals = ({
  mismatches,
  unmatched,
  allSuperseded,
}: {
  mismatches: number
  unmatched: number
  allSuperseded: boolean
}) => {
  if (mismatches > 0 && unmatched > 0) {
    return `${mismatches} ${mismatches === 1 ? 'balance' : 'balances'} and ${unmatched} ${unmatched === 1 ? 'correction' : 'corrections'} need review`
  }
  if (mismatches > 0) {
    return mismatches === 1
      ? '1 balance doesn’t match its transfers'
      : `${mismatches} balances don’t match their transfers`
  }
  // A superseded correction is stale, not pending. Saying it "isn't applied"
  // sends people to Recompute, which is what stranded it in the first place.
  if (allSuperseded) {
    return unmatched === 1
      ? '1 correction is out of date'
      : `${unmatched} corrections are out of date`
  }
  return unmatched === 1
    ? '1 correction isn’t applied to lots'
    : `${unmatched} corrections aren’t applied to lots`
}

export const LotTrustSignalsBanner = ({
  signals,
  isRecomputing,
  isStarting,
  onRecompute,
  onViewDetails,
}: {
  signals: LotTrustSignals
  isRecomputing: boolean
  isStarting: boolean
  onRecompute: () => void
  onViewDetails: () => void
}) => {
  // Pending rebuilds can temporarily leave facts/assertions ahead of the
  // projection; the progress banner covers them.
  if (isRecomputing || isStarting) return null
  const mismatches = (signals.reconciliation ?? []).filter(
    (row) => BigInt(row.delta) !== 0n,
  ).length
  const unmatched = signals.unmatchedAssertions.length
  if (mismatches === 0 && unmatched === 0) return null
  // Only when nothing else is outstanding: a real mismatch still wants a
  // rebuild even if a stale correction happens to sit alongside it.
  const allSuperseded =
    mismatches === 0 &&
    unmatched > 0 &&
    signals.unmatchedAssertions.every((row) => row.superseded)

  return (
    <div role="status">
      <Message
        variant="warning"
        action={
          allSuperseded
            ? { label: 'View details', onClick: onViewDetails }
            : [
                {
                  label: 'View details',
                  variant: 'link',
                  onClick: onViewDetails,
                },
                { label: 'Recompute lots', onClick: onRecompute },
              ]
        }
      >
        {describeSignals({ mismatches, unmatched, allSuperseded })}
      </Message>
    </div>
  )
}
